import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TimelineSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: 'Our Journey',
      subtitle: 'Milestones in Our Legal Excellence',
      timeline: [
        {
          year: '2014',
          title: 'Firm Establishment',
          description: 'Peter & Partners Advocates LLP was founded with a vision to provide exceptional legal services in Kenya.',
          icon: 'Building',
          achievements: [
            'Registered with Law Society of Kenya',
            'Established first office in Nairobi',
            'Recruited founding team of 3 lawyers'
          ]
        },
        {
          year: '2016',
          title: 'Practice Expansion',
          description: 'Expanded our practice areas to include corporate law, family law, and property law.',
          icon: 'TrendingUp',
          achievements: [
            'Added corporate law division',
            'Handled first major merger case',
            'Team grew to 8 professionals'
          ]
        },
        {
          year: '2018',
          title: 'Community Engagement',
          description: 'Launched pro bono programs and community legal education initiatives.',
          icon: 'Heart',
          achievements: [
            'Started free legal clinic',
            'Conducted 50+ community workshops',
            'Helped 200+ individuals with legal aid'
          ]
        },
        {
          year: '2020',
          title: 'Digital Transformation',
          description: 'Embraced technology to enhance client service delivery and case management.',
          icon: 'Smartphone',
          achievements: [
            'Implemented digital case management',
            'Launched virtual consultation services',
            'Developed client portal system'
          ]
        },
        {
          year: '2022',
          title: 'Recognition & Awards',
          description: 'Received multiple awards for legal excellence and community service.',
          icon: 'Award',
          achievements: [
            'Top Rated Law Firm award',
            'Community Impact recognition',
            'Client satisfaction rating of 98%'
          ]
        },
        {
          year: '2024',
          title: 'Continued Growth',
          description: 'Expanding our reach and impact while maintaining our commitment to excellence.',
          icon: 'Rocket',
          achievements: [
            'Serving 1000+ clients annually',
            'Team of 15+ legal professionals',
            'Planning second office location'
          ]
        }
      ]
    },
    sw: {
      title: 'Safari Yetu',
      subtitle: 'Hatua Muhimu katika Ubora Wetu wa Kisheria',
      timeline: [
        {
          year: '2014',
          title: 'Kuanzishwa kwa Kampuni',
          description: 'Peter & Partners Advocates LLP ilianzishwa na maono ya kutoa huduma za kipekee za kisheria nchini Kenya.',
          icon: 'Building',
          achievements: [
            'Ilisajiliwa na Chama cha Sheria cha Kenya',
            'Kuanzisha ofisi ya kwanza Nairobi',
            'Kuajiri timu ya msingi ya mawakili 3'
          ]
        },
        {
          year: '2016',
          title: 'Upanuzi wa Utendaji',
          description: 'Tulipanua maeneo yetu ya utendaji kujumuisha sheria ya makampuni, sheria ya familia, na sheria ya mali.',
          icon: 'TrendingUp',
          achievements: [
            'Kuongeza idara ya sheria ya makampuni',
            'Kushughulikia kesi ya kwanza kubwa ya muungano',
            'Timu ilikua hadi wataalamu 8'
          ]
        },
        {
          year: '2018',
          title: 'Kushirikiana na Jamii',
          description: 'Tulizindua mipango ya pro bono na miradi ya elimu ya kisheria ya jamii.',
          icon: 'Heart',
          achievements: [
            'Kuanza kliniki ya bure ya kisheria',
            'Kufanya warsha 50+ za jamii',
            'Kusaidia watu 200+ na msaada wa kisheria'
          ]
        },
        {
          year: '2020',
          title: 'Mabadiliko ya Kidijitali',
          description: 'Tulikubali teknolojia ili kuboresha utoaji wa huduma kwa wateja na usimamizi wa kesi.',
          icon: 'Smartphone',
          achievements: [
            'Kutekeleza usimamizi wa kidijitali wa kesi',
            'Kuzindua huduma za mazungumzo ya mtandaoni',
            'Kuunda mfumo wa mlango wa mteja'
          ]
        },
        {
          year: '2022',
          title: 'Utambuzi na Tuzo',
          description: 'Tulipokea tuzo nyingi kwa ubora wa kisheria na huduma ya jamii.',
          icon: 'Award',
          achievements: [
            'Tuzo ya Kampuni ya Sheria ya Hali ya Juu',
            'Utambuzi wa Athari ya Jamii',
            'Kiwango cha kuridhisha wateja cha 98%'
          ]
        },
        {
          year: '2024',
          title: 'Ukuaji Unaoendelea',
          description: 'Tunapanua ufikaji na athari yetu huku tukidumisha kujitolea kwetu kwa ubora.',
          icon: 'Rocket',
          achievements: [
            'Kutumikia wateja 1000+ kila mwaka',
            'Timu ya wataalamu 15+ wa kisheria',
            'Kupanga eneo la pili la ofisi'
          ]
        }
      ]
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface/5 to-primary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-xl font-heading font-semibold text-primary">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 lg:left-1/2 lg:transform lg:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary"></div>

          <div className="space-y-12">
            {currentContent.timeline.map((milestone, index) => (
              <div key={index} className={`relative flex items-start ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              }`}>
                {/* Timeline Dot */}
                <div className="absolute left-4 lg:left-1/2 lg:transform lg:-translate-x-1/2 w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center z-10">
                  <Icon name={milestone.icon} size={16} color="white" />
                </div>

                {/* Content */}
                <div className={`ml-16 lg:ml-0 lg:w-1/2 ${
                  index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'
                }`}>
                  <div className="card p-6 space-y-4 hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-heading font-bold text-primary">
                        {milestone.year}
                      </span>
                      <div className="h-px bg-border flex-1"></div>
                    </div>
                    
                    <h3 className="text-xl font-heading font-semibold text-text-primary">
                      {milestone.title}
                    </h3>
                    
                    <p className="text-text-secondary leading-relaxed">
                      {milestone.description}
                    </p>

                    <div className="space-y-2">
                      <h4 className="text-sm font-heading font-semibold text-text-primary">
                        {currentLanguage === 'en' ? 'Key Achievements:' : 'Mafanikio Makuu:'}
                      </h4>
                      <ul className="space-y-1">
                        {milestone.achievements.map((achievement, achievementIndex) => (
                          <li key={achievementIndex} className="flex items-start space-x-2">
                            <Icon name="Check" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-text-secondary">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Spacer for desktop */}
                <div className="hidden lg:block lg:w-1/2"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Future Vision */}
        <div className="mt-16 text-center">
          <div className="card p-8 bg-gradient-to-br from-primary/5 to-accent/5">
            <Icon name="Telescope" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-text-primary mb-4">
              {currentLanguage === 'en' ? 'Looking Forward' : 'Kuangalia Mbele'}
            </h3>
            <p className="text-text-secondary leading-relaxed max-w-3xl mx-auto">
              {currentLanguage === 'en' ?'As we continue to grow and evolve, our commitment to legal excellence, client satisfaction, and community impact remains unwavering. We look forward to serving Kenya\'s legal needs for many years to come.'
                : 'Tunapoendelea kukua na kubadilika, kujitolea kwetu kwa ubora wa kisheria, kuridhisha wateja, na athari ya jamii kunabaki bila kubadilika. Tunasubiri kutumikia mahitaji ya kisheria ya Kenya kwa miaka mingi ijayo.'
              }
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;